import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import type * as React from "react";

import { cn } from "@krea/shared/utils";

import { Button } from "../Button";

const ResponsiveDialog = DialogPrimitive.Root;

const ResponsiveDialogTrigger = DialogPrimitive.Trigger;

const ResponsiveDialogClose = DialogPrimitive.Close;

const ResponsiveDialogPortal = DialogPrimitive.Portal;

const ResponsiveDialogOverlay = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    className={cn(
      "inset-0 fixed z-400000 bg-black/50 backdrop-blur-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
);
ResponsiveDialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const ResponsiveDialogVariants = cva(
  cn(
    "fixed z-400001 gap-4 bg-white p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 overflow-y-auto",
    // NOTE: no slide-in/out on the centered (lg) dialog. In v4, `translate-x-[-50%]`
    // is the `translate` CSS *property*, while `slide-*` animates the `transform`
    // property — the two add up, so a slide-in-from-top/left made the modal start
    // at the top-left corner and travel to center. zoom + fade don't touch
    // translation, so the modal scales/fades in place, centered.
    "lg:left-[50%] lg:top-[50%] lg:grid lg:w-full lg:max-w-2xl lg:translate-x-[-50%] lg:translate-y-[-50%] lg:border lg:duration-200 lg:data-[state=open]:animate-in lg:data-[state=closed]:animate-out lg:data-[state=closed]:fade-out-0 lg:data-[state=open]:fade-in-0 lg:data-[state=closed]:zoom-out-95 lg:data-[state=open]:zoom-in-95 lg:rounded-xl",
  ),
  {
    variants: {
      // Slides are scoped to max-lg: — they apply only to the mobile sheet
      // (positioned by inset, no translate-centering, so transform-slide is safe).
      // On lg the dialog is centered via the `translate` property and uses
      // zoom+fade only (see note above), so a transform-slide here would re-add
      // the top-left offset.
      side: {
        top: "inset-x-0 top-0 max-h-[90%] rounded-b-xl border-b max-lg:data-[state=closed]:slide-out-to-top max-lg:data-[state=open]:slide-in-from-top lg:h-fit",
        bottom:
          "inset-x-0 bottom-0 max-h-[90%] rounded-t-xl border-t max-lg:pr-14 max-lg:data-[state=closed]:slide-out-to-bottom max-lg:data-[state=open]:slide-in-from-bottom lg:h-fit",
        left: "inset-y-0 left-0 h-full w-3/4 rounded-r-xl border-r max-lg:data-[state=closed]:slide-out-to-left max-lg:data-[state=open]:slide-in-from-left sm:max-w-sm lg:h-fit",
        right:
          "inset-y-0 right-0 h-full w-3/4 rounded-l-xl border-l max-lg:data-[state=closed]:slide-out-to-right max-lg:data-[state=open]:slide-in-from-right sm:max-w-sm lg:h-fit",
      },
    },
    defaultVariants: {
      side: "bottom",
    },
  },
);

interface IResponsiveDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof ResponsiveDialogVariants> {
  elevatedStacking?: boolean;
  /** Portal container element. Defaults to document.body. Pass the widget root to keep portaled content inside scoped styles. */
  container?: HTMLElement | null;
}

const ResponsiveDialogContent = ({
  side = "bottom",
  className,
  children,
  elevatedStacking = false,
  container,
  ref,
  ...props
}: IResponsiveDialogContentProps & {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Content>>;
}) => (
  <ResponsiveDialogPortal container={container ?? undefined}>
    <ResponsiveDialogOverlay className={cn(elevatedStacking && "z-500000")}>
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          ResponsiveDialogVariants({ side }),
          elevatedStacking && "z-500001",
          className,
        )}
        aria-describedby={undefined}
        {...props}
      >
        <DialogPrimitive.Title className="sr-only">
          Dialog
        </DialogPrimitive.Title>
        {children}

        <ResponsiveDialogClose asChild>
          <Button
            variant="ghost"
            size="icon"
            id="-default-dialog-close-icon"
            aria-label="Close"
            className="absolute right-3 top-3 size-11 md:size-10"
          >
            <X className="size-6" strokeWidth={2} />
            <span className="sr-only">Close</span>
          </Button>
        </ResponsiveDialogClose>
      </DialogPrimitive.Content>
    </ResponsiveDialogOverlay>
  </ResponsiveDialogPortal>
);
ResponsiveDialogContent.displayName = DialogPrimitive.Content.displayName;

const ResponsiveDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
ResponsiveDialogHeader.displayName = "ResponsiveDialogHeader";

const ResponsiveDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
ResponsiveDialogFooter.displayName = "ResponsiveDialogFooter";

const ResponsiveDialogTitle = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-section", className)}
    {...props}
  />
);
ResponsiveDialogTitle.displayName = DialogPrimitive.Title.displayName;

const ResponsiveDialogDescription = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-gray-600", className)}
    {...props}
  />
);
ResponsiveDialogDescription.displayName =
  DialogPrimitive.Description.displayName;

const ResponsiveDialogContentPrimitive = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) => (
  <DialogPrimitive.Content
    ref={ref}
    className={cn(className)}
    aria-describedby={undefined}
    {...props}
  />
);

export {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogContentPrimitive,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogOverlay,
  ResponsiveDialogPortal,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
};
